import { AnimatePresence, m as motion } from 'framer-motion'
import { Box, Button, Stack, Typography, theme } from '@dtx-company/design-system/src'
import { FC, ReactNode, useCallback, useState } from 'react'
import { IconComponent } from '@dtx-company/design-system/src/types/util'
import { Login } from '@mui/icons-material'
import { NavigationMenu } from '../../../../components/layout/header/v2/MarketingNavLinks'
import { NextLinkWrap } from '@app/page/src/components/util/NextLinkUtils'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import {
  TOP_NAV_MOBILE_LINKS,
  TopNavSlice
} from '../../../../components/layout/header/v2/topNavData'
import { closeMenuIcon, flowcodeNavLogo } from '@app/common/src/constants/assets'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { setNavLeftMenu } from '@dtx-company/inter-app/src/redux/slices/config-slice'
import { useDispatch } from 'react-redux'
import { useNavigationRoutes } from '../../../../hooks/useNavigationRoutes'
import { useRouter } from 'next/router'
import Collapse from '@mui/material/Collapse'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Image from 'next/image'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'
import useTranslation from 'next-translate/useTranslation'

export interface MobileNavMenuProps {
  open?: boolean
  items?: MobileNavMenuItemProps[]
  userIsLoggedInOnMobile?: boolean
}

interface MobileNavMenuItemProps {
  header?: string
  subItems?: MobileNavMenuSubItemProps[]
}

interface MobileNavMenuSubItemProps {
  header: string
  subHeader?: string
  link?: string
}
export const MobileMarketingNavMenu: FC<MobileNavMenuProps> = props => {
  const { t } = useTranslation('nav')
  const dispatch = useDispatch()
  const setMenuClosed = useCallback(() => dispatch(setNavLeftMenu({ open: false })), [dispatch])
  const router = useRouter()
  const [expendedItemsIds, setExpendedItemsIds] = useState<string | undefined>(undefined)

  return (
    <Stack
      direction="column"
      gap={9}
      sx={{
        flexDirection: 'column',
        height: '100vh',
        overflowY: 'auto',
        p: 4,
        width: '100vw',
        zIndex: 1001
      }}
      data-testid="marketing-nav-mobile"
    >
      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
        <Image src={flowcodeNavLogo} alt="logo" width={121} height={24} />
        <Image
          role="button"
          src={closeMenuIcon}
          onClick={() => {
            events.Global_Navigation_Closed_MobileNav()
            setMenuClosed()
          }}
          width={21}
          height={21}
          alt="close"
        />
      </Stack>
      <Stack gap={10}>
        <Stack justifyContent={'start'}>
          {TOP_NAV_MOBILE_LINKS.map(item => (
            <MobileNavMenuItem
              key={item.primary.header}
              item={item}
              showSubItems={expendedItemsIds}
              setShowSubItems={setExpendedItemsIds}
            />
          ))}
        </Stack>
        {props.userIsLoggedInOnMobile ? (
          <Button
            size="large"
            fullWidth
            color="tertiary"
            data-testid="mobile-dashboard-button"
            startIcon={Login}
            href={Routes.ANALYTICS_CODES}
            onClick={() => {
              setMenuClosed()
              events.Global_MarketingNav_Clicked_DashboardButton()
            }}
          >
            {t('Header.v2.Dashboard')}
          </Button>
        ) : (
          <Stack
            direction="row"
            gap={2}
            sx={{
              a: {
                width: '100%'
              }
            }}
          >
            <NextLinkWrap flexShrink={0} data-testid="sign-up" href={Routes.SIGN_UP}>
              <Button
                size="large"
                fullWidth
                color="tertiary"
                onClick={() => {
                  events.Global_Navigation_MobileNav_Clicked_SignUp()
                  setMenuClosed()
                }}
                data-testid="marketing-nav-mobile-signup"
              >
                {t('Header.v2.SignUpMobile')}
              </Button>
            </NextLinkWrap>

            <NextLinkWrap
              flexShrink={0}
              data-testid="sign-in"
              href={{
                pathname: Routes.SIGN_IN,
                query: { ...router.query }
              }}
            >
              <Button
                fullWidth
                size="large"
                variant={'outlined'}
                color="secondary"
                onClick={() => {
                  events.Global_Navigation_MobileNav_Clicked_SignIn()
                  setMenuClosed()
                }}
                data-testid="marketing-nav-mobile-signin"
              >
                {t('Header.v2.SignIn')}
              </Button>
            </NextLinkWrap>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export const MobileLeftNav: FC<MobileNavMenuProps> = props => {
  const { userIsLoggedInOnMobile } = useNavigationRoutes()

  return (
    <MobileLeftNavWrapper open={props.open}>
      <MobileMarketingNavMenu {...props} userIsLoggedInOnMobile={userIsLoggedInOnMobile} />
    </MobileLeftNavWrapper>
  )
}

const MobileLeftNavWrapper: FC<{ open?: boolean; children: ReactNode }> = ({ open, children }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ translateX: '-100vw' }}
        exit={{ translateX: '-100vw' }}
        animate={{ translateX: '0px' }}
        transition={{ duration: 0.25 }}
        style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100001,
          backgroundColor: theme.palette.background.paper,
          width: '100%',
          overflowY: 'hidden',
          maxHeight: '100vh'
        }}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
)

const MobileNavMenuItem: FC<{
  item: TopNavSlice
  showSubItems?: string
  setShowSubItems: (id?: string) => void
}> = ({ item, showSubItems, setShowSubItems }) => {
  const getIcon = (item: TopNavSlice): IconComponent | undefined => {
    if (item.items.length === 0) return undefined
    return showSubItems === item.primary.header ? ExpandLessIcon : ExpandMoreIcon
  }
  return (
    <Box
      sx={theme => ({
        '&': { width: 'max-content' },
        'button, a': {
          borderBottom: `2px solid transparent`,
          textDecorator: 'none'
        },
        'a:hover, button:hover': {
          backgroundColor: 'transparent',
          borderBottom: `2px solid ${theme.palette.tertiary.main}`,
          color: theme.palette.tertiary.main
        }
      })}
    >
      <Button
        variant={'text'}
        data-testid={`marketing-nav-mobile-${item.primary.header}`}
        onClick={() => {
          fireAnalyticsEvent('Global_Navigation_Clicked_MobileNav', {
            value: item.primary.header
          })
          if (showSubItems !== item.primary.header) setShowSubItems(item.primary.header)
          else setShowSubItems(undefined)
        }}
        color={showSubItems === item.primary.header ? 'tertiary' : 'primary'}
        href={item.primary.header_link}
        endIcon={getIcon(item)}
      >
        <Typography variant={'subtitleLarge'}>{item.primary.header}</Typography>
      </Button>
      <Collapse in={showSubItems === item.primary.header} timeout="auto">
        <Stack sx={{ pl: 4 }}>
          <NavigationMenu navItem={item} />
        </Stack>
      </Collapse>
    </Box>
  )
}
